/* eslint-disable react/jsx-no-useless-fragment */
import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { FaArrowLeft } from 'react-icons/fa';

import Loading from '../../../components/Loading';

import { Container } from '../../../styles/layout/Container';
import { SmallButton } from '../../../styles/ui/Button';
import { TextLink } from '../../../styles/ui/Link';
import {
   PageHeader,
   PageHeaderActions,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
} from '../../../styles/layout/PageHeader';

import ItemCustomerPanel from './components/ItemCustomerPanel';
import ItemEditFormPanel from './components/ItemEditFormPanel';
import ItemStatusPanel from './components/ItemStatusPanel';
import ItemCommissionsPanel from './components/ItemCommissionsPanel';
import MissingIntermediatesPanel from './components/MissingIntermediatesPanel';
import CancelItemDialog from './components/CancelItemDialog';
import RestoreItemDialog from './components/RestoreItemDialog';
import ItemStatusBadge from '../RoutingItemList/components/ItemStatusBadge';
import PeriodBadge from '../RoutingItemList/components/PeriodBadge';

import {
   getScheduleItemById,
   updateScheduleItem,
} from '../../../services/routing/routingApi';
import { getDepartments } from '../../../services/hr/hrApi';

// =============================================================================
// Tela 2 — Detalhe do item de roteirização.
// Orquestra os painéis empilhados:
//   1. Cliente (somente leitura — snapshot da importação, não editável)
//   2. Dados do serviço (esquerda read-only: técnico/serviços; direita
//      editável: origem/pagamento/observações — carrega o botão Editar)
//   3. Status (transição terminal — PATCH /routing/items/:id/status)
//   4. Comissões (CRUD básico — fluxo independente)
//
// Postura de lançamento v1.1 (ADR-010 / handoff 0052): o perfil financeiro
// (routing:finance:operate) PODE trocar status e marcar eventos terminais nesta
// ficha, pra desafogar o operador de rota enquanto a eficiência dele amadurece.
// O gating é por CHAVE DE PERMISSÃO (canChangeStatus), NUNCA por role id — a
// reversão futura é só por grants, sem deploy do front. Backend é a defesa real
// (guards do §2.3 do handoff: terminal_overwrite_forbidden, missing_technician,
// commission_locked, item_locked_active_event, etc.).
//
// O ciclo Editar/Cancelar/Salvar vive aqui, mas os botões são renderizados
// DENTRO da seção editável (painel 2), única parte editável do item. Salvar
// dispara um único PUT /routing/items/:id com a whitelist de campos de origem/
// pagamento/observações.
// =============================================================================

const BackLink = styled(TextLink).attrs({ as: 'button', type: 'button' })`
   font-size: 0.8rem;
   padding: 4px 0;
   margin-bottom: 8px;
`;

const TitleRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
`;

const TitleSubInfo = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   flex-wrap: wrap;
`;

const CancelButton = styled(SmallButton).attrs({ $intent: 'danger' })``;

const CancelledBanner = styled.div`
   background: rgba(0, 0, 0, 0.04);
   border: 1px solid rgba(0, 0, 0, 0.12);
   border-radius: 10px;
   padding: 12px 14px;
   margin-bottom: 12px;
   font-size: 0.85rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#444'};
   line-height: 1.5;

   strong {
      font-weight: 600;
      color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};
   }
`;

function formatDateBr(isoDate) {
   if (!isoDate) return '';
   const [year, month, day] = String(isoDate).slice(0, 10).split('-');
   return `${day}/${month}/${year}`;
}

function formatDateTimeBr(isoString) {
   if (!isoString) return '—';
   const date = new Date(isoString);
   if (Number.isNaN(date.getTime())) return '—';
   const day = String(date.getDate()).padStart(2, '0');
   const month = String(date.getMonth() + 1).padStart(2, '0');
   const year = date.getFullYear();
   const hours = String(date.getHours()).padStart(2, '0');
   const minutes = String(date.getMinutes()).padStart(2, '0');
   return `${day}/${month}/${year} ${hours}:${minutes}`;
}

// Estado inicial do formulário combinado (Painel Cliente + Painel Edição).
// Whitelist espelha exatamente ScheduleItemService.update#allowedFields no backend.
function getInitialFormData(item) {
   if (!item) return null;
   return {
      // Painel Cliente — editáveis
      customer_name: item.customer_name ?? '',
      customer_phone: item.customer_phone ?? '',
      // Painel Edição — pós-import
      department_id: item.department_id ?? '',
      assigned_technician_employee_id:
         item.assigned_technician_employee_id ?? null,
      is_freelance_technician: item.is_freelance_technician === 1 ? 1 : 0,
      seller_employee_id: item.seller_employee_id ?? null,
      is_salesperson_external: item.is_salesperson_external === 1 ? 1 : 0,
      payment_collection_type: item.payment_collection_type ?? '',
      // Seleção de serviços (handoff 0039) — substitui as 4 colunas *_qty.
      services: (item.services ?? []).map((s) => ({
         commission_table_id: s.commission_table_id,
         service_code: s.service_code,
         service_description: s.service_description,
         quantity: s.quantity,
      })),
      notes: item.notes ?? '',
   };
}

// =============================================================================

export default function RoutingItemShow() {
   const { itemId } = useParams();
   const navigate = useNavigate();
   const location = useLocation();

   const permissions = useSelector(
      (state) => state.auth.user?.permissions || []
   );
   const canRead = permissions.includes('routing:schedule_item:read');
   const canEdit = permissions.includes('routing:schedule_item:update');

   // M3 §2: o perfil financeiro (routing:finance:operate) opera a ficha sem ter
   // as permissões da logística. Os endpoints aceitam OR(perm_logística,
   // finance:operate) — então o botão aparece se o usuário tiver QUALQUER chave.
   const canFinanceOperate = permissions.includes('routing:finance:operate');

   // Trocar status / marcar terminal na ficha. Gating por CHAVE (update_status
   // OU finance:operate — override ADR-010), nunca por role id. §4.1 do 0052.
   const canChangeStatus =
      permissions.includes('routing:schedule_item:update_status') ||
      canFinanceOperate;
   // Inserir intermediário faltante (POST events). M3 §2/§4.
   const canRecordEvent =
      permissions.includes('routing:schedule:record_event') ||
      canFinanceOperate;
   // Lançar/listar comissão. Editar/remover seguem SÓ na logística (ver
   // ItemCommissionsPanel.canRemove). M3 §2.
   const canCreateCommission =
      permissions.includes('routing:commission_entry:create') ||
      canFinanceOperate;
   const canRemoveCommission = permissions.includes(
      'routing:commission_entry:delete'
   );

   // Deletar item: cancel (logística) OU finance:operate. Trava UNIVERSAL do
   // handoff 0059: item roteirizado (route_subcluster_id != null) não é
   // excluível por NENHUM perfil — 422 item_already_routed (o code finance-
   // específico finance_cannot_delete_routed segue disparando antes pro
   // financeiro). Restaurar (reverter o cancelamento) é EXCLUSIVO de quem tem
   // cancel — sem OR com finance:operate, assimetria intencional (decisão de
   // produto 2026-07-07: "a origem vem da roteirização, a correção também").
   const canCancelPerm = permissions.includes('routing:schedule_item:cancel');
   const canDeleteItem = canCancelPerm || canFinanceOperate;

   // ─── Estado: dados ──────────────────────────────────────────
   const [item, setItem] = useState(null);
   const [departments, setDepartments] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [notFound, setNotFound] = useState(false);

   // ─── Estado: edição (lift dos painéis 1 e 2) ──────────────
   const [isEditing, setIsEditing] = useState(false);
   const [isSaving, setIsSaving] = useState(false);
   const [formData, setFormData] = useState(null);
   const [fieldErrors, setFieldErrors] = useState({});

   // ─── Estado: dialogs ────────────────────────────────────────
   const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
   const [isRestoreDialogOpen, setIsRestoreDialogOpen] = useState(false);

   // Item bloqueado: cancelado não permite editar/mudar status/lançar comissão.
   // Backend é a defesa real (422); aqui é só UX para esconder ações inúteis.
   const isCancelled = item?.item_status === 'CANCELADO';

   // Item roteirizado (pertence a uma rota/subcluster). Não é excluível por
   // nenhum perfil (trava universal do 0059) — botão desabilitado com o motivo
   // no title; o 422 item_already_routed é a rede.
   const isRouted = item?.route_subcluster_id != null;

   // Motivo do bloqueio do "Cancelar item" quando visível-mas-desabilitado —
   // orienta em vez de desabilitar mudo (mesmo padrão dos locks do StopDrawer).
   let deleteLockHint = null;
   if (isRouted) {
      deleteLockHint =
         'Item já está em uma rota. Remova-o da rota antes de excluí-lo.';
   } else if (item && item.item_status !== 'PENDENTE') {
      deleteLockHint =
         'Não é possível cancelar um item Realizado ou Não realizado. Volte o status para Pendente primeiro (e remova as comissões antes, se houver).';
   }

   // ─── Fetch do item ──────────────────────────────────────────
   const fetchItem = useCallback(async () => {
      if (!itemId) return;
      setIsLoading(true);
      try {
         const fetched = await getScheduleItemById(itemId);
         if (!fetched) {
            setNotFound(true);
            return;
         }
         setItem(fetched);
         setNotFound(false);
      } catch (err) {
         if (err?.response?.status === 404) {
            setNotFound(true);
            return;
         }
         const apiMessage =
            err?.response?.data?.message ??
            err?.message ??
            'Erro ao carregar item de agenda.';
         toast.error(apiMessage);
      } finally {
         setIsLoading(false);
      }
   }, [itemId]);

   useEffect(() => {
      fetchItem();
   }, [fetchItem]);

   // Sincroniza formData com item — só quando NÃO está em modo edição,
   // pra não atropelar edições do usuário caso o item refetche em paralelo
   // (ex: callback de mudança de status). Em save, o handler limpa isEditing
   // antes do refetch, então o reset acontece como esperado.
   useEffect(() => {
      if (!isEditing) {
         setFormData(getInitialFormData(item));
      }
   }, [item, isEditing]);

   // ─── Fetch dos departamentos (para o select do Painel 2) ───
   // Departamentos só alimentam o <select> da edição. Quem não pode editar
   // (ex.: perfil financeiro — só permissões de routing, sem
   // routing:schedule_item:update) não precisa deles E tomaria 403 na chamada
   // cross-module GET /hr/departments (não tem permissão de HR), disparando o
   // redirect global pra /403. O rótulo read-only vem de item.department.
   useEffect(() => {
      if (!canEdit) return undefined;
      let cancelled = false;
      getDepartments()
         .then((list) => {
            if (cancelled) return;
            setDepartments(Array.isArray(list) ? list : []);
         })
         .catch(() => {
            if (cancelled) return;
            setDepartments([]);
         });
      return () => {
         cancelled = true;
      };
   }, [canEdit]);

   // ─── Handlers de edição ─────────────────────────────────────
   const updateField = useCallback((field, value) => {
      setFormData((prev) => (prev ? { ...prev, [field]: value } : prev));
      setFieldErrors((prev) => {
         if (!prev[field]) return prev;
         const next = { ...prev };
         delete next[field];
         return next;
      });
   }, []);

   const handleStartEditing = () => {
      setFieldErrors({});
      setIsEditing(true);
   };

   const handleCancelEditing = () => {
      setFormData(getInitialFormData(item));
      setFieldErrors({});
      setIsEditing(false);
   };

   const handleSave = async () => {
      if (isSaving || !formData) return;

      // Validação client-side mínima (espelha backend)
      const clientErrors = {};
      // Mutual exclusion técnico/freelance e vendedor/externo. A UI já desabilita
      // reciprocamente — este guard é defesa em profundidade caso o estado fique
      // inconsistente (ex: item legado carregado com ambos preenchidos).
      // Item roteirizado não edita técnico/freelance pela ficha (handoff 0042
      // §7 — definido pela atribuição da rota); o guard de exclusão mútua só
      // vale para item avulso.
      if (
         !isRouted &&
         formData.assigned_technician_employee_id != null &&
         formData.is_freelance_technician === 1
      ) {
         clientErrors.assigned_technician_employee_id =
            'Selecione um técnico OU marque "Freelance", nunca ambos.';
      }
      if (
         formData.seller_employee_id != null &&
         formData.is_salesperson_external === 1
      ) {
         clientErrors.seller_employee_id =
            'Selecione um vendedor OU marque "Vendedor externo", nunca ambos.';
      }
      if (Object.keys(clientErrors).length > 0) {
         setFieldErrors(clientErrors);
         toast.error('Verifique os campos destacados.');
         return;
      }

      // Cliente, serviços e observações NÃO entram no payload: dados do cliente
      // são snapshot read-only da importação, os serviços previstos e as
      // observações são definidos pela roteirização/vendedor. A ficha edita só
      // origem (vendedor/departamento) e pagamento (cobrança).
      const payload = {
         department_id: formData.department_id || null,
         seller_employee_id: formData.seller_employee_id || null,
         is_salesperson_external: formData.is_salesperson_external ? 1 : 0,
         payment_collection_type: formData.payment_collection_type || null,
      };

      // Técnico/freelance: enviados SÓ para item avulso. Item roteirizado tem o
      // técnico definido pela atribuição da rota (handoff 0042 §7) — enviar aqui
      // dispararia 422 technician_owned_by_route.
      if (!isRouted) {
         payload.assigned_technician_employee_id =
            formData.assigned_technician_employee_id || null;
         payload.is_freelance_technician = formData.is_freelance_technician
            ? 1
            : 0;
      }

      setIsSaving(true);
      setFieldErrors({});

      try {
         const result = await updateScheduleItem(item.id, payload);
         toast.success(result.message || 'Item atualizado com sucesso.');
         setIsEditing(false);
         // Refetch garante coerência das associations
         // (assignedTechnician, seller, department, etc.).
         fetchItem();
      } catch (err) {
         // 422 com errors[]: mapeia para erros por campo
         const responseErrors = err?.response?.data?.errors;
         if (Array.isArray(responseErrors) && responseErrors.length > 0) {
            const errorsByField = {};
            responseErrors.forEach((entry) => {
               if (entry?.field) errorsByField[entry.field] = entry.message;
            });
            setFieldErrors(errorsByField);
            toast.error(
               err?.response?.data?.message ??
                  'Verifique os campos destacados.'
            );
         } else {
            const apiMessage =
               err?.response?.data?.message ??
               err?.message ??
               'Erro ao salvar item.';
            toast.error(apiMessage);

            // 422 sem errors[] = regra de negócio (ex: item cancelado por outro
            // usuário no meio do caminho). Sai do modo edição e re-busca pra
            // sincronizar a UI com o estado atual do item no servidor.
            if (err?.response?.status === 422) {
               setIsEditing(false);
               fetchItem();
            }
         }
      } finally {
         setIsSaving(false);
      }
   };

   const handleStatusChanged = useCallback(() => {
      fetchItem();
   }, [fetchItem]);

   // Pós-cancelamento (sucesso) e race-condition (422 dispara onCancelled(null))
   // levam ao mesmo lugar: re-busca o item pra que a tela transicione pra modo
   // "cancelado" (banner + painéis ocultos) ou pro estado atual do servidor.
   const handleItemCancelled = useCallback(() => {
      fetchItem();
   }, [fetchItem]);

   // Restauração (handoff 0059) — mesmo desfecho do cancelamento: re-busca o
   // item pra tela transicionar de volta ao modo normal (banner some, painéis
   // retornam, metadados do cancelamento já vêm apagados do backend).
   const handleItemRestored = useCallback(() => {
      fetchItem();
   }, [fetchItem]);

   // location.key === 'default' significa "primeira entrada da sessão"
   // (deep-link externo, F5 direto no detalhe, ou nova aba). Nesses casos
   // não há histórico pra voltar, então cai pro fallback fixo. Caso
   // contrário, navigate(-1) preserva os filtros da listagem que vieram
   // por trás na URL.
   const handleBack = () => {
      if (location.key !== 'default') {
         navigate(-1);
      } else {
         navigate('/routing/items');
      }
   };

   // ─── Guards ─────────────────────────────────────────────────
   if (!canRead) {
      return (
         <Container>
            <BackLink type="button" onClick={handleBack}>
               <FaArrowLeft size={12} /> Voltar para listagem
            </BackLink>
            <PageHeader>
               <PageHeaderText>
                  <PageTitle>Sem permissão</PageTitle>
                  <PageSubtitle>
                     Você não tem acesso ao módulo de Roteirização.
                  </PageSubtitle>
               </PageHeaderText>
            </PageHeader>
         </Container>
      );
   }

   if (notFound) {
      return (
         <Container>
            <BackLink type="button" onClick={handleBack}>
               <FaArrowLeft size={12} /> Voltar para listagem
            </BackLink>
            <PageHeader>
               <PageHeaderText>
                  <PageTitle>Item não encontrado</PageTitle>
                  <PageSubtitle>
                     O item solicitado não existe ou foi removido.
                  </PageSubtitle>
               </PageHeaderText>
            </PageHeader>
         </Container>
      );
   }

   // ─── Render ─────────────────────────────────────────────────
   return (
      <Container>
         <Loading isLoading={isLoading || isSaving} />

         <BackLink type="button" onClick={handleBack}>
            <FaArrowLeft size={12} /> Voltar para listagem
         </BackLink>

         <PageHeader>
            <PageHeaderText>
               <TitleRow>
                  <PageTitle>
                     {item?.order_number
                        ? `Pedido ${item.order_number}`
                        : 'Item de agenda'}
                  </PageTitle>
                  {item?.item_status && (
                     <ItemStatusBadge
                        value={item.item_status}
                        size="lg"
                        onDark
                     />
                  )}
               </TitleRow>
               {item && (
                  <PageSubtitle as="div">
                     <TitleSubInfo>
                        <span>{formatDateBr(item.schedule_date)}</span>
                        <span>·</span>
                        <PeriodBadge
                           value={item.schedule_period}
                           size="md"
                           onDark
                        />
                        {item.importBatch?.original_filename && (
                           <>
                              <span>·</span>
                              <span style={{ opacity: 0.7 }}>
                                 Lote: {item.importBatch.original_filename}
                              </span>
                           </>
                        )}
                     </TitleSubInfo>
                  </PageSubtitle>
               )}
            </PageHeaderText>

            {item && (
               <PageHeaderActions>
                  {canDeleteItem && !isEditing && !isCancelled && (
                     <CancelButton
                        type="button"
                        onClick={() => setIsCancelDialogOpen(true)}
                        disabled={Boolean(deleteLockHint)}
                        title={deleteLockHint ?? 'Cancelar este item da rota'}
                     >
                        Cancelar item
                     </CancelButton>
                  )}
                  {isCancelled && canCancelPerm && (
                     <SmallButton
                        type="button"
                        $intent="write"
                        onClick={() => setIsRestoreDialogOpen(true)}
                        title="Restaurar este item — volta a Pendente, no balde Não roteirizados"
                     >
                        Restaurar item
                     </SmallButton>
                  )}
               </PageHeaderActions>
            )}
         </PageHeader>

         {item && formData && (
            <>
               {isCancelled && (
                  <CancelledBanner>
                     Item <strong>cancelado</strong> em{' '}
                     <strong>{formatDateTimeBr(item.cancelled_at)}</strong>
                     {item.cancelledByUser?.nome ? (
                        <>
                           {' '}por <strong>{item.cancelledByUser.nome}</strong>
                        </>
                     ) : null}
                     . Edição, mudança de status e lançamento de comissões estão
                     bloqueados.
                  </CancelledBanner>
               )}

               {!isCancelled && (
                  <MissingIntermediatesPanel
                     item={item}
                     canRecordEvent={canRecordEvent}
                     onInserted={fetchItem}
                  />
               )}

               <ItemCustomerPanel item={item} />

               <ItemEditFormPanel
                  item={item}
                  departments={departments}
                  formData={formData}
                  onFieldChange={updateField}
                  isEditing={isEditing}
                  isSaving={isSaving}
                  fieldErrors={fieldErrors}
                  isRouted={isRouted}
                  canEdit={canEdit && !isCancelled}
                  onStartEditing={handleStartEditing}
                  onCancelEditing={handleCancelEditing}
                  onSave={handleSave}
               />

               {!isCancelled && (
                  <ItemStatusPanel
                     item={item}
                     onStatusChanged={handleStatusChanged}
                     canChangeStatus={canChangeStatus}
                  />
               )}

               {!isCancelled && canCreateCommission && (
                  <ItemCommissionsPanel
                     item={item}
                     canCreate={canCreateCommission}
                     canRemove={canRemoveCommission}
                  />
               )}
            </>
         )}

         <CancelItemDialog
            isOpen={isCancelDialogOpen}
            onClose={() => setIsCancelDialogOpen(false)}
            item={item}
            onCancelled={handleItemCancelled}
         />

         <RestoreItemDialog
            isOpen={isRestoreDialogOpen}
            onClose={() => setIsRestoreDialogOpen(false)}
            item={item}
            onRestored={handleItemRestored}
         />
      </Container>
   );
}
